/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { getApiConfig } from '../../environment';
import { AuditStatus, QuestionType, ActionTriggerType, TriggerCondition } from '../../../../pages/Compliance/Enums/complianceEnums';
import {
  AuditType,
  AuditTemplate,
  Audit,
  ActionTrigger,
  CreateAuditRequest,
  UpdateAuditRequest,
  SubmitAuditRequest,
  ApproveAuditRequest,
  ChallengeAuditRequest,
  AuditSchedule,
  ActionItem,
  JobRole,
  ComplianceNotification,
  DashboardMetrics,
  AuditScoreTrend,
  Store,
  Region,
  ComplianceFranchise,
  SuccessResponse,
  UpdateAuditResponseRequest,
  Auditor,
  ScheduleForm,
  AuditReportFilter,
  AuditSummaryReport,
  AuditDetailReport,
  ActionItemsSummaryReport,
  ComplianceOverviewReport,
} from '../../../../pages/Compliance/Interfaces/complicanceInterfaces';

const apiConfig = getApiConfig();
const baseUrl = apiConfig.apiBaseUrl;
const instance = axios.create({
  baseURL: baseUrl,
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  },
});

async function getAuthHeaders() {
  const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
  return {
    Authorization: token ? `Bearer ${token}` : '',
  };
}

type HttpMethod = 'get' | 'post' | 'put' | 'delete';

async function axiosWithAuth<T>(
  method: HttpMethod,
  url: string,
  dataOrConfig?: any,
  config?: any,
): Promise<SuccessResponse<T>> {
  const headers = await getAuthHeaders();

  const axiosConfig = {
    ...config,
    headers,
  };

  let res;

  switch (method) {
    case 'get':
      res = await instance.get<SuccessResponse<T>>(url, { ...axiosConfig, ...dataOrConfig });
      break;
    case 'post':
      res = await instance.post<SuccessResponse<T>>(url, dataOrConfig, axiosConfig);
      break;
    case 'put':
      res = await instance.put<SuccessResponse<T>>(url, dataOrConfig, axiosConfig);
      break;
    case 'delete':
      res = await instance.delete<SuccessResponse<T>>(url, axiosConfig);
      break;
    default:
      throw new Error(`Unsupported method: ${method}`);
  }

  return res.data;
}

// ========================================
// AUDITORS
// ========================================
export async function getAllAuditors(franchiseIds?: number[], regionIds?: number[], storeNumbers?: string[]): Promise<Auditor[]> {
  const body: Record<string, unknown> = {};
  if (franchiseIds && franchiseIds.length > 0) {
    body.franchiseIds = franchiseIds;
  }
  if (regionIds && regionIds.length > 0) {
    body.regionIds = regionIds;
  }
  if (storeNumbers && storeNumbers.length > 0) {
    body.storeNumbers = storeNumbers;
  }
  const res = await axiosWithAuth<Auditor[]>('post', '/compliance/reference/auditors/search', body);
  return res.data;
}

// ========================================
// COMPLIANCE REFERENCE CONTROLLER
// (new endpoints at /compliance/stores & /compliance/regions)
// ========================================
export async function getComplianceStores(includeInactive: boolean = false, franchiseIds?: number[], search?: string, take?: number): Promise<Store[]> {
  const body: Record<string, unknown> = { includeInactive };
  if (franchiseIds && franchiseIds.length > 0) {
    body.franchiseIds = franchiseIds;
  }
  if (search) {
    body.search = search;
  }
  if (take) {
    body.take = take;
  }
  const res = await axiosWithAuth<Store[]>('post', '/compliance/stores/search', body);
  return res.data;
}

export async function getComplianceStoreById(id: number): Promise<Store> {
  const res = await axiosWithAuth<Store>('get', `/compliance/stores/${id}`);
  return res.data;
}

export async function getComplianceStoreByNumber(storeNumber: string): Promise<Store> {
  const res = await axiosWithAuth<Store>('get', `/compliance/stores/number/${storeNumber}`);
  return res.data;
}

export async function getComplianceRegions(franchiseIds?: number[]): Promise<Region[]> {
  const body: Record<string, unknown> = {};
  if (franchiseIds && franchiseIds.length > 0) {
    body.franchiseIds = franchiseIds;
  }
  const res = await axiosWithAuth<Region[]>('post', '/compliance/regions/search', body);
  return res.data;
}

// ========================================
// TENANT FRANCHISES
// ========================================
export async function getTenantFranchises(storeNumber: string): Promise<ComplianceFranchise[]> {
  const res = await axiosWithAuth<ComplianceFranchise[]>('get', '/compliance/franchises', {
    params: { storeNumber },
  });
  return res.data;
}

// ========================================
// DASHBOARD
// ========================================
export async function getDashboardMetrics(storeNumbers?: string[], regions?: string[]): Promise<DashboardMetrics> {
  const res = await axiosWithAuth<DashboardMetrics>('get', '/compliance/dashboard/metrics', {
    params: { storeNumbers, regions },
  });
  return res.data;
}

export async function getAuditScoreTrends(months: number = 6, storeNumbers?: string[], regions?: string[]): Promise<AuditScoreTrend[]> {
  const res = await axiosWithAuth<AuditScoreTrend[]>('get', '/compliance/dashboard/audit-score-trends', {
    params: { months, storeNumbers, regions },
  });
  return res.data;
}

// ========================================
// AUDIT TYPES
// ========================================
export async function getAllAuditTypes(includeInactive: boolean = false, storeNumber?: string): Promise<AuditType[]> {
  const res = await axiosWithAuth<AuditType[]>('get', '/compliance/audit-types', { params: { includeInactive, storeNumber } });
  return res.data;
}

export async function getAuditTypeById(id: number, storeNumber?: string): Promise<AuditType> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditType>('get', `/compliance/audit-types/${id}`, { params });
  return res.data;
}

export async function createAuditType(data: Omit<AuditType, 'id' | 'createdDate' | 'updatedDate'>, storeNumber?: string): Promise<AuditType> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditType>('post', '/compliance/audit-types', data, { params });
  return res.data;
}

export async function updateAuditType(id: number, data: Partial<AuditType>, storeNumber?: string): Promise<AuditType> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditType>('put', `/compliance/audit-types/${id}`, data, { params });
  return res.data;
}

export async function deleteAuditType(id: number, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('delete', `/compliance/audit-types/${id}`, { params });
}

// ========================================
// AUDIT TEMPLATES
// ========================================
export async function getAllAuditTemplates(auditTypeId?: number, includeUnpublished: boolean = false, storeNumber?: string, franchiseId?: number): Promise<AuditTemplate[]> {
  const res = await axiosWithAuth<AuditTemplate[]>('get', '/compliance/audit-templates', {
    params: { auditTypeId, includeUnpublished, storeNumber, franchiseId },
  });
  return res.data.map(normalizeTemplate);
}

export async function getAuditTemplateById(id: number, storeNumber?: string): Promise<AuditTemplate> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditTemplate>('get', `/compliance/audit-templates/${id}`, { params });
  return normalizeTemplate(res.data);
}

export async function createAuditTemplate(data: Omit<AuditTemplate, 'id' | 'createdDate' | 'publishedDate'>, storeNumber?: string): Promise<AuditTemplate> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditTemplate>('post', '/compliance/audit-templates', data, { params });
  return normalizeTemplate(res.data);
}

export async function updateAuditTemplate(id: number, data: Partial<AuditTemplate>, storeNumber?: string): Promise<AuditTemplate> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditTemplate>('put', `/compliance/audit-templates/${id}`, data, { params });
  return normalizeTemplate(res.data);
}

export async function deleteAuditTemplate(id: number, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('delete', `/compliance/audit-templates/${id}`, { params });
}

export async function publishAuditTemplate(id: number, storeNumber?: string): Promise<AuditTemplate> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditTemplate>('post', `/compliance/audit-templates/${id}/publish`, null, { params });
  return normalizeTemplate(res.data);
}

// ========================================
// NORMALIZERS
// ========================================
/**
 * The API returns all enum fields as PascalCase string names
 * (e.g. "InProgress", "Boolean", "RequireNote", "Equals").
 * Parse them into the numeric enum values the frontend expects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseEnum<T extends Record<string, any>>(enumObj: T, raw: unknown, fallback: T[keyof T]): T[keyof T] {
  if (typeof raw === 'number') return raw as T[keyof T];
  if (typeof raw === 'string') {
    // Exact key match first (e.g. "Submitted" -> AuditStatus.Submitted)
    const byKey = enumObj[raw as keyof T];
    if (typeof byKey === 'number') return byKey as T[keyof T];
    // Numeric string fallback (e.g. "2" -> 2)
    const asNum = Number(raw);
    if (!isNaN(asNum)) return asNum as T[keyof T];
  }
  return fallback;
}

function normalizeAudit(audit: Audit): Audit {
  const assignedUsers = audit.assignedUsers ?? [];
  // The API sends assignedToUserId/assignedToUserName as null (not undefined),
  // so use || to fall back through both null and undefined to the assignedUsers array.
  const derivedUserName = assignedUsers.map(u => u.userName).join(', ') || undefined;
  const derivedUserId = assignedUsers[0]?.userId;

  // Normalize action trigger data on responses
  const normalizedResponses = (audit.responses || []).map(r => {
    const normalized = { ...r };

    // Ensure actionPhotoUrls is always an array (backend may send null)
    if (r.actionPhotoUrls === null || r.actionPhotoUrls === undefined) {
      normalized.actionPhotoUrls = undefined;
    }

    // Ensure actionSignatureUrl is a string or undefined (not null)
    if (r.actionSignatureUrl === null) {
      normalized.actionSignatureUrl = undefined;
    }

    if (r.actionTriggerPhotos === null || r.actionTriggerPhotos === undefined) {
      normalized.actionTriggerPhotos = undefined;
    }

    if (r.actionTriggerSignatures === null || r.actionTriggerSignatures === undefined) {
      normalized.actionTriggerSignatures = undefined;
    }

    if (r.actionItemDetails) {
      normalized.actionItemDetails = {
        ...r.actionItemDetails,
        dueDate: r.actionItemDetails.dueDate
          ? r.actionItemDetails.dueDate.substring(0, 10) // ISO → YYYY-MM-DD
          : r.actionItemDetails.dueDate,
      };
    }

    return normalized;
  });

  // Map backend field names to frontend interface names
  const raw = audit as any;
  return {
    ...audit,
    status: parseEnum(AuditStatus, audit.status, AuditStatus.Draft),
    assignedToUserId: audit.assignedToUserId || derivedUserId,
    assignedToUserName: audit.assignedToUserName || derivedUserName,
    template: audit.template ? normalizeTemplate(audit.template) : audit.template,
    responses: normalizedResponses,
    // Backend sends FirstSignatureUrl / SecondSignatureUrl; frontend interface uses firstSignatureData / secondSignatureData
    firstSignatureData: audit.firstSignatureData ?? raw.firstSignatureUrl ?? null,
    secondSignatureData: audit.secondSignatureData ?? raw.secondSignatureUrl ?? null,
  };
}

function normalizeTemplate(template: AuditTemplate): AuditTemplate {
  return {
    ...template,
    sections: template.sections?.map(section => ({
      ...section,
      questions: section.questions?.map(normalizeQuestion) ?? [],
    })) ?? [],
  };
}

function normalizeQuestion<T extends { questionType: QuestionType; actionTriggers?: ActionTrigger[] }>(question: T): T {
  return {
    ...question,
    questionType: parseEnum(QuestionType, question.questionType, QuestionType.Text),
    actionTriggers: question.actionTriggers
      ?.filter((t: any) => !t.deletedOn)
      .map(trigger => ({
        ...trigger,
        triggerType: parseEnum(ActionTriggerType, trigger.triggerType, ActionTriggerType.RequireNote),
        triggerCondition: parseEnum(TriggerCondition, trigger.triggerCondition, TriggerCondition.Equals),
      }))
      .sort((a: any, b: any) => (a.id ?? 0) - (b.id ?? 0)) ?? [],
  };
}

// ========================================
// AUDITS (use /Audit/Audit endpoints)
// ========================================
export async function getAllAudits(
  storeNumber?: string,
  status?: number,
  startDate?: string,
  endDate?: string,
): Promise<Audit[]> {
  const params: any = {};
  if (storeNumber !== undefined) params.storeNumber = storeNumber;
  if (status !== undefined) params.status = status;
  if (startDate !== undefined) params.startDate = startDate;
  if (endDate !== undefined) params.endDate = endDate;

  const res = await axiosWithAuth<Audit[]>('get', '/compliance/audits', {
    params,
  });
  return res.data.map(normalizeAudit);
}

export async function getAuditById(id: number, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber !== undefined) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('get', `/compliance/audits/${id}`, { params });
  return normalizeAudit(res.data);
}

export async function createAudit(data: CreateAuditRequest, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('post', '/compliance/audits', data, { params });
  return normalizeAudit(res.data);
}

export async function updateAudit(id: number, data: UpdateAuditRequest, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('put', `/compliance/audits/${id}`, data, { params });
  return normalizeAudit(res.data);
}

export async function submitAudit(id: number, data: SubmitAuditRequest, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('post', `/compliance/audits/${id}/submit`, data, { params });
  return normalizeAudit(res.data);
}

export async function approveAudit(id: number, data: ApproveAuditRequest, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('post', `/compliance/audits/${id}/approve`, data, { params });
  return normalizeAudit(res.data);
}

export async function challengeAudit(id: number, data: ChallengeAuditRequest, storeNumber?: string): Promise<Audit> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<Audit>('post', `/compliance/audits/${id}/challenge`, data, { params });
  return normalizeAudit(res.data);
}

export async function deleteAudit(id: number, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber !== undefined) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('delete', `/compliance/audits/${id}`, { params });
}

export async function updateAuditResponses(
  auditId: number,
  data: UpdateAuditResponseRequest,
  storeNumber?: string,
): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>(
    'put',
    `/compliance/audits/${auditId}/responses`,
    data,
    { params },
  );
}

export async function uploadAuditPhoto(
  auditId: number,
  questionId: number,
  file: File | Blob,
  storeNumber?: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const params: any = { questionId };
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<{ url: string }>(
    'post',
    `/compliance/audits/${auditId}/photos/upload`,
    formData,
    { params },
  );
  return res.data.url;
}

export async function downloadAuditPdf(auditId: number, storeNumber?: string): Promise<void> {
  const headers = await getAuthHeaders();
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;

  const response = await instance.get(`/compliance/audits/${auditId}/pdf`, {
    params,
    headers,
    responseType: 'blob',
  });

  // Extract filename from content-disposition header, or use a fallback
  const contentDisposition = response.headers['content-disposition'];
  let fileName = `Audit_${auditId}.pdf`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)(\1)/);
    if (match?.[2]) {
      fileName = decodeURIComponent(match[2]);
    }
  }

  // Trigger browser download
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function previewAuditPdf(auditId: number, storeNumber?: string): Promise<void> {
  const headers = await getAuthHeaders();
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;

  const response = await instance.get(`/compliance/audits/${auditId}/pdf`, {
    params,
    headers,
    responseType: 'blob',
  });

  // Open in new browser tab for preview
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Revoke after a delay to give the browser time to load the PDF
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

// ========================================
// AUDIT SCHEDULES
// ========================================
export async function getAllAuditSchedules(templateId?: number, storeId?: number, includeInactive: boolean = false, storeNumber?: string): Promise<AuditSchedule[]> {
  const res = await axiosWithAuth<AuditSchedule[]>('get', '/compliance/audit-schedules', {
    params: { templateId, storeId, includeInactive, storeNumber },
  });
  return res.data;
}

export async function getAuditScheduleById(id: number, storeNumber?: string): Promise<AuditSchedule> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditSchedule>('get', `/compliance/audit-schedules/${id}`, { params });
  return res.data;
}

export async function createAuditSchedule(data: Omit<ScheduleForm, 'id' | 'createdDate' | 'updatedDate' | 'templateName' | 'storeName' | 'nextScheduledDate'>, storeNumber?: string): Promise<AuditSchedule> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditSchedule>('post', '/compliance/audit-schedules', data, { params });
  return res.data;
}

export async function updateAuditSchedule(id: number, data: Partial<ScheduleForm>, storeNumber?: string): Promise<AuditSchedule> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<AuditSchedule>('put', `/compliance/audit-schedules/${id}`, data, { params });
  return res.data;
}

export async function deleteAuditSchedule(id: number, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('delete', `/compliance/audit-schedules/${id}`, { params });
}

export async function triggerAuditSchedule(id: number, storeNumber?: string): Promise<number> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<number>('post', `/compliance/audit-schedules/${id}/trigger`, {}, { params });
  return res.data;
}

// ========================================
// ACTION ITEMS
// ========================================
export async function getAllActionItems(storeNumber?: string, status?: string, assignedToUserId?: number): Promise<ActionItem[]> {
  const res = await axiosWithAuth<ActionItem[]>('get', '/compliance/action-items', {
    params: { storeNumber, status, assignedToUserId },
  });
  return res.data;
}

export async function getActionItemById(id: number, storeNumber?: string): Promise<ActionItem> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<ActionItem>('get', `/compliance/action-items/${id}`, { params });
  return res.data;
}

export async function createActionItem(data: Omit<ActionItem, 'id' | 'createdDate' | 'resolvedDate' | 'resolvedBy'>): Promise<ActionItem> {
  const res = await axiosWithAuth<ActionItem>('post', '/compliance/action-items', data);
  return res.data;
}

export async function updateActionItem(id: number, data: Partial<ActionItem>, storeNumber?: string): Promise<ActionItem> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<ActionItem>('put', `/compliance/action-items/${id}`, data, { params });
  return res.data;
}

export async function resolveActionItem(id: number, resolutionNotes: string, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('post', `/compliance/action-items/${id}/resolve`, {
    resolutionNotes,
  }, { params });
}

export async function closeActionItem(id: number, storeNumber: string | undefined, closeNotes: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('post', `/compliance/action-items/${id}/close`, { closeNotes }, { params });
}

export async function addActionItemFeedback(id: number, storeNumber: string | undefined, feedback: string): Promise<ActionItem> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  const res = await axiosWithAuth<ActionItem>('post', `/compliance/action-items/${id}/feedback`, { feedback }, { params });
  return res.data;
}

export async function deleteActionItem(id: number, storeNumber?: string): Promise<void> {
  const params: any = {};
  if (storeNumber) params.storeNumber = storeNumber;
  await axiosWithAuth<void>('delete', `/compliance/action-items/${id}`, { params });
}

// ========================================
// JOB ROLES
// ========================================
export async function getAllJobRoles(includeInactive: boolean = false): Promise<JobRole[]> {
  const res = await axiosWithAuth<JobRole[]>('get', '/compliance/job-roles', { params: { includeInactive } });
  return res.data;
}

export async function getJobRoleById(id: number): Promise<JobRole> {
  const res = await axiosWithAuth<JobRole>('get', `/compliance/job-roles/${id}`);
  return res.data;
}

export async function createJobRole(data: Omit<JobRole, 'id'>): Promise<JobRole> {
  const res = await axiosWithAuth<JobRole>('post', '/compliance/job-roles', data);
  return res.data;
}

export async function updateJobRole(id: number, data: Partial<JobRole>): Promise<JobRole> {
  const res = await axiosWithAuth<JobRole>('put', `/compliance/job-roles/${id}`, data);
  return res.data;
}

export async function deleteJobRole(id: number): Promise<void> {
  await axiosWithAuth<void>('delete', `/compliance/job-roles/${id}`);
}

// ========================================
// NOTIFICATIONS
// ========================================
export async function getNotificationsByUserId(userId: number, includeRead: boolean = true): Promise<ComplianceNotification[]> {
  const res = await axiosWithAuth<ComplianceNotification[]>('get', `/compliance/notifications/user/${userId}`, {
    params: { includeRead },
  });
  return res.data;
}

export async function markNotificationAsRead(id: number): Promise<ComplianceNotification> {
  const res = await axiosWithAuth<ComplianceNotification>('post', `/compliance/notifications/${id}/read`);
  return res.data;
}

export async function markAllNotificationsAsRead(userId: number): Promise<void> {
  await axiosWithAuth<void>('post', `/compliance/notifications/user/${userId}/read-all`);
}

// ========================================
// AUDIT REPORTS
// ========================================

export async function generateAuditSummaryReport(filter: AuditReportFilter): Promise<AuditSummaryReport> {
  const res = await axiosWithAuth<AuditSummaryReport>('post', '/compliance/reports/audit-summary', filter);
  return res.data;
}

export async function previewAuditSummaryReport(filter: AuditReportFilter): Promise<AuditSummaryReport> {
  const res = await axiosWithAuth<AuditSummaryReport>('post', '/compliance/reports/audit-summary/preview', filter);
  return res.data;
}

export async function exportAuditSummaryReport(filter: AuditReportFilter): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await instance.post('/compliance/reports/audit-summary/export', filter, {
    headers,
    responseType: 'blob',
  });
  return response.data;
}

export async function generateAuditDetailReport(filter: AuditReportFilter): Promise<AuditDetailReport> {
  const res = await axiosWithAuth<AuditDetailReport>('post', '/compliance/reports/audit-details', filter);
  return res.data;
}

export async function previewAuditDetailReport(filter: AuditReportFilter): Promise<AuditDetailReport> {
  const res = await axiosWithAuth<AuditDetailReport>('post', '/compliance/reports/audit-details/preview', filter);
  return res.data;
}

export async function exportAuditDetailReport(filter: AuditReportFilter): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await instance.post('/compliance/reports/audit-details/export', filter, {
    headers,
    responseType: 'blob',
  });
  return response.data;
}

export async function generateActionItemsSummaryReport(filter: AuditReportFilter): Promise<ActionItemsSummaryReport> {
  const res = await axiosWithAuth<ActionItemsSummaryReport>('post', '/compliance/reports/action-items', filter);
  return res.data;
}

export async function previewActionItemsSummaryReport(filter: AuditReportFilter): Promise<ActionItemsSummaryReport> {
  const res = await axiosWithAuth<ActionItemsSummaryReport>('post', '/compliance/reports/action-items/preview', filter);
  return res.data;
}

export async function exportActionItemsSummaryReport(filter: AuditReportFilter): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await instance.post('/compliance/reports/action-items/export', filter, {
    headers,
    responseType: 'blob',
  });
  return response.data;
}

export async function generateComplianceOverviewReport(filter: AuditReportFilter): Promise<ComplianceOverviewReport> {
  const res = await axiosWithAuth<ComplianceOverviewReport>('post', '/compliance/reports/compliance-overview', filter);
  return res.data;
}

export async function previewComplianceOverviewReport(filter: AuditReportFilter): Promise<ComplianceOverviewReport> {
  const res = await axiosWithAuth<ComplianceOverviewReport>('post', '/compliance/reports/compliance-overview/preview', filter);
  return res.data;
}

export async function exportComplianceOverviewReport(filter: AuditReportFilter): Promise<Blob> {
  const headers = await getAuthHeaders();
  const response = await instance.post('/compliance/reports/compliance-overview/export', filter, {
    headers,
    responseType: 'blob',
  });
  return response.data;
}
